<template>
  <transition name="announcement-slide">
    <aside
      v-if="isVisible"
      class="announcement-bar"
      role="region"
      aria-label="Announcement"
    >
        <div class="announcement-container">
          <div
            class="announcement-content"
            @click="handleNavigate"
            tabindex="0"
            role="link"
            @keydown.enter="handleNavigate"
            @keydown.space.prevent="handleNavigate"
          >
            <span class="announcement-badge">
              <span class="sparkle-icon" aria-hidden="true">✨</span>
              <span class="badge-text">{{ announcement.tag }}</span>
            </span>

            <span class="announcement-title">
              {{ announcement.title }}
            </span>

            <span class="announcement-cta" v-if="announcement.linkText">
              <span>{{ announcement.linkText }}</span>
              <svg
                class="cta-arrow"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </span>
          </div>

          <button
            class="announcement-close-btn"
            @click.stop="handleDismiss"
            type="button"
            aria-label="Dismiss announcement"
            title="Dismiss announcement"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </aside>
    </transition>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from '#imports';
import { currentAnnouncement, type AnnouncementItem } from '@/constants/announcement';

const props = withDefaults(
  defineProps<{
    announcement?: AnnouncementItem;
  }>(),
  {
    announcement: () => currentAnnouncement,
  }
);

const emit = defineEmits<{
  (e: 'dismiss'): void;
  (e: 'navigate', link: string): void;
}>();

const router = useRouter();
const isVisible = ref(props.announcement.enabled);

const STORAGE_KEY_PREFIX = 'hopeai_dismissed_announcement_';

onMounted(() => {
  if (!props.announcement.enabled) {
    isVisible.value = false;
    return;
  }

  try {
    const isDismissed = localStorage.getItem(`${STORAGE_KEY_PREFIX}${props.announcement.id}`);
    if (isDismissed) {
      isVisible.value = false;
    }
  } catch {
    // Keep visible if localStorage inaccessible
  }
});

const handleDismiss = () => {
  isVisible.value = false;
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${props.announcement.id}`, 'true');
  } catch {}
  emit('dismiss');
};

const handleNavigate = () => {
  const link = props.announcement.link;
  emit('navigate', link);
  if (!link) return;

  if (link.startsWith('http://') || link.startsWith('https://')) {
    window.open(link, '_blank', 'noopener,noreferrer');
  } else {
    router.push(link);
  }
};
</script>

<style scoped lang="scss">
.announcement-bar {
  width: 100%;
  position: relative;
  background: linear-gradient(
    90deg,
    rgba(23, 12, 38, 0.98) 0%,
    rgba(58, 22, 94, 0.95) 25%,
    rgba(104, 54, 168, 0.9) 50%,
    rgba(58, 22, 94, 0.95) 75%,
    rgba(23, 12, 38, 0.98) 100%
  );
  border-bottom: 1px solid rgba(168, 85, 247, 0.3);
  box-shadow: 0 4px 20px rgba(104, 54, 168, 0.25);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  z-index: 1002;
  transition: all 0.3s ease;
}

.announcement-container {
  max-width: 1300px;
  margin: 0 auto;
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 48px;
  position: relative;
}

.announcement-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
  text-decoration: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
  outline: none;

  &:hover {
    .announcement-title {
      color: #ffffff;
      text-shadow: 0 0 12px rgba(255, 255, 255, 0.3);
    }

    .cta-arrow {
      transform: translateX(4px);
    }

    .announcement-badge {
      background: rgba(192, 132, 252, 0.35);
      border-color: rgba(233, 213, 255, 0.6);
      box-shadow: 0 0 14px rgba(168, 85, 247, 0.45);
    }
  }

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid #c084fc;
    outline-offset: 4px;
  }
}

.announcement-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 10px;
  border-radius: 9999px;
  background: rgba(147, 51, 234, 0.25);
  border: 1px solid rgba(192, 132, 252, 0.4);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #f3e8ff;
  box-shadow: 0 0 8px rgba(147, 51, 234, 0.25);
  transition: all 0.25s ease;
  flex-shrink: 0;

  .sparkle-icon {
    font-size: 11px;
    line-height: 1;
    display: inline-block;
    filter: drop-shadow(0 0 4px rgba(233, 213, 255, 0.8));
    animation: gentle-pulse 2.4s ease-in-out infinite;
  }
}

.announcement-title {
  font-size: 13px;
  font-weight: 500;
  color: #f1f5f9;
  letter-spacing: 0.15px;
  line-height: 1.4;
  transition: color 0.2s ease, text-shadow 0.2s ease;
  text-align: center;
}

.announcement-cta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #d8b4fe;
  letter-spacing: 0.2px;
  flex-shrink: 0;
  transition: color 0.2s ease;

  .cta-arrow {
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
}

.announcement-close-btn {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  outline: none;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    transform: translateY(-50%) scale(1.1);
  }

  &:focus-visible {
    outline: 2px solid #c084fc;
    outline-offset: 2px;
  }
}

/* Animations */
@keyframes gentle-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.18);
    opacity: 1;
  }
}

.announcement-slide-enter-active,
.announcement-slide-leave-active {
  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.25s ease,
              padding 0.35s ease;
  max-height: 80px;
  overflow: hidden;
}

.announcement-slide-enter-from,
.announcement-slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* Responsive adjustments */
@media (max-width: 900px) {
  .announcement-container {
    padding: 6px 42px 6px 16px;
  }

  .announcement-content {
    gap: 8px;
  }

  .announcement-title {
    font-size: 12px;
    max-width: 480px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .announcement-cta {
    font-size: 11px;
  }
}

@media (max-width: 640px) {
  .announcement-container {
    min-height: 38px;
    padding: 5px 36px 5px 12px;
  }

  .announcement-badge {
    padding: 2px 7px;
    font-size: 10px;
  }

  .announcement-title {
    font-size: 12px;
    max-width: 240px;
  }

  .announcement-cta span {
    display: none; // Keep just arrow or concise on small screens
  }

  .announcement-close-btn {
    right: 8px;
    width: 22px;
    height: 22px;
  }
}
</style>
